import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import SwitchSelector from 'react-switch-selector';
import { showLoader, hideLoader } from '../../../store/common/slice';
import { isAdminSelector } from '../../../store/auth/selectors';
import { constants } from '../../../common/constants/constants';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import { newSelectStyles } from '../../../common/shared/select.styled';
import { getSelectMonthForDropdown } from '../../../common/shared/statelessMethods';
import CacheData from '../../../common/utils/cacheData';
import useBodyClass from '../../../common/hooks/useBodyClassName';
import Search from '../../../images/search.svg';
import { StyledReport } from '../../reports/report.styled';
import ApprovedActions from './ApprovedActions';
import PendingActions from './PendingActions';
import { BankAdjustmentsWrapper, BankAdjustmentsTabs } from './BankAdjustmentsNew.styled';

const BankAdjustments = ({ history, match }) => {
    const { t } = useTranslation();
    const client = useAccessToken();
    const dispatch = useDispatch();
    const isAdmin = useSelector(isAdminSelector);
    const ltr = useSelector(state => state.common.ltrEnabled);
    const tab = CacheData.fetchCache('bankAdjustmentsTab');
    const { buildingID, year, month } = match.params;

    document.title = t('titles.bank_adjustments');

    useBodyClass('report');

    const [filter, setFilter] = useState('');
    const [currentTab, setCurrentTab] = useState(tab || 'approved');
    const [monthData, setMonthData] = useState({
        monthsTexts: [],
        selectedIndex: 0
    });
    const [transactions, setTransactions] = useState([]);
    const [approvedTransactions, setApprovedTransactions] = useState([]);
    const [pendingTransactions, setPendingTransactions] = useState([]);
    const titleData = useSelector(state => state.buildings.titleData);
    
    const paymentStates = constants.APIContract.paymentStates;
    const SUCCESS_STATUSES = paymentStates.SUCCESS_STATUSES;
    const PENDING_STATUSES = paymentStates.PENDING_STATUSES;

    const loadTransactions = async (buildingId, month, year) => {
        dispatch(showLoader());
        const response = await client.get(`/managers/getAllTransactions/${buildingId}/${month}/${year}`);
        response.transactions?.forEach(item => {
            item.isIncome ? item.type = t('expenses.income') : item.type = t('expenses.expense');
            item.method ? item.methodText = t(`buildings.${item.method}`) : item.methodText = t(`buildings.${item.Method}`);
        });
        dispatch(hideLoader());
        return response.transactions;
    };

    useEffect(async () => {
        if (!client) return;

        dispatch(showLoader());
        const monthsTexts = getSelectMonthForDropdown(year, t);
        setMonthData({
            monthsTexts,
            selectedIndex: monthsTexts.findIndex(item => item.value === `${parseInt(month)}_${year}`)
        });
        const data = await loadTransactions(buildingID, month, year);
        setTransactions(data);
        dispatch(hideLoader());
    }, [client]);

    useEffect(() => {
        let approvedItems = [];
        let pendingItems = [];
        for (const item of transactions) {
            const value = item.PaymentState || item.expenseState;
            if (SUCCESS_STATUSES.includes(value)) approvedItems.push(item)
            else if (PENDING_STATUSES.includes(value)) pendingItems.push(item)
        }
        approvedItems.sort((a, b) => a.chargeDate || a.PaymentDate > b.chargeDate || b.PaymentDate ? 1 : -1);
        pendingItems.sort((a, b) => a.chargeDate || a.PaymentDate > b.chargeDate || b.PaymentDate ? 1 : -1);
        setApprovedTransactions(approvedItems);
        setPendingTransactions(pendingItems);
    }, [transactions]);

    const onMonthAndYearChanged = async monthYearData => {
        const parts = monthYearData.value.split('_');
        const newMonth = parts[0];
        const newYear = parts[1];
        const selectedIndex = monthData.monthsTexts.findIndex(option => option.value === `${parseInt(newMonth)}_${newYear}`)
        setMonthData({
            ...monthData,
            selectedIndex
        });
        const newUrl = `/${constants.pages.bankAdjustmentsUrl}/${buildingID}/${newMonth}/${newYear}`;
        history.push({ pathname: newUrl });
        const response = await loadTransactions(buildingID, newMonth, newYear);
        setTransactions(response);
    };

    const components = {
        approved: {
            component: ApprovedActions,
            props: {
                transactions: approvedTransactions,
                filter
            }
        },
        pending: {
            component: PendingActions,
            props: {
                transactions: pendingTransactions,
                buildingID,
                year,
                month
            }
        }
    };
    const tabComponent = components[currentTab];

    return (
        <div className="ensure-height">
            <h1 className='page-title text-only'>
                    <span>{t('expenses.bank_adjustments.title')}</span>
                    {
                        titleData.translationKey && (
                            <span className={'building-title'}>
                                {t('payments.' + titleData.translationKey, titleData)}
                            </span>
                        )
                    }
                </h1>
            <BankAdjustmentsWrapper>
                <div className={`bank_adjustments_header ${currentTab === 'pending' ? 'border-bottom' : ''}`}>
                    <div className='input-wrapper'>
                        {currentTab === 'approved' && (
                            <StyledReport className='report' ltr={ltr}>
                                <div className='report-wrap'>
                                    <div className='report-top'>
                                        <div className='report-top-start'>
                                            <div className="search">                     
                                                <input
                                                    type="text"
                                                    name="search"
                                                    onChange={e => setFilter(e.target.value.toLowerCase())}
                                                />
                                                <img src={Search} alt={t('search.search')} />                    
                                            </div>                                      
                                        </div>
                                    </div>
                                </div>
                            </StyledReport>
                        )}
                    </div>

                    <BankAdjustmentsTabs className={currentTab === 'approved' ? 'approved-selected' : ''}>
                        <SwitchSelector
                            initialSelectedIndex={tab === 'pending' ? 1 : 2}
                            options={[
                                {
                                    label: t('expenses.approved'),
                                    value: 'approved',
                                    selectedBackgroundColor: 'white',
                                },
                                {
                                    label: t('expenses.pending'),
                                    value: 'pending',
                                    selectedBackgroundColor: 'white',
                                }
                            ]}
                            selectedFontColor='#6E74FF'
                            fontColor='#ffffff'
                            backgroundColor='linear-gradient(90deg, #6C90FF -0.23%, #8567FF 92.15%)'
                            onChange={e => setCurrentTab(e)}
                        />
                    </BankAdjustmentsTabs>

                    <Select
                        className="select"
                        styles={newSelectStyles}
                        options={monthData.monthsTexts}
                        value={monthData.monthsTexts[monthData.selectedIndex]}
                        onChange={onMonthAndYearChanged}
                    />
                </div>

                <tabComponent.component
                    {...tabComponent.props}
                />
            </BankAdjustmentsWrapper>
        </div>
    );
};

export default BankAdjustments;
